import { t } from 'lib/i18n'

export interface QueuedMutation {
  id: string
  label: string
  query: string
  variables?: Record<string, unknown>
  queuedAt: string
}

const STORAGE_KEY = 'jopath.offlineQueue'

const read = (): QueuedMutation[] => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]')
  } catch {
    return []
  }
}

type Listener = (queue: QueuedMutation[]) => void
const listeners = new Set<Listener>()

const write = (queue: QueuedMutation[]) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(queue))
  } catch {
    // best-effort only; losing the queue on storage failure (private mode, quota) is no worse than today's behaviour
  }
  listeners.forEach((listener) => listener(queue))
}

/** Lets components (the offline banner) react to the pending-count without polling. */
export const subscribeQueue = (listener: Listener): (() => void) => {
  listeners.add(listener)
  listener(read())
  return () => {
    listeners.delete(listener)
  }
}

export const getQueue = (): QueuedMutation[] => read()

const mutationLabel = (query: string): string => {
  const body = query.replace(/^\s*mutation[^{]*/, '')
  return /\{\s*(\w+)/.exec(body)?.[1] ?? t('تعديل', 'Change')
}

/** Called from client.ts only when `fetch` itself fails (genuinely offline) on a mutation —
 *  never for server-returned errors, which would just fail again on replay. */
export const enqueueMutation = (query: string, variables?: Record<string, unknown>): void => {
  const queue = read()
  queue.push({ id: crypto.randomUUID(), label: mutationLabel(query), query, queuedAt: new Date().toISOString(), variables })
  write(queue)
}

export const removeFromQueue = (id: string): void => {
  write(read().filter((item) => item.id !== id))
}

let processing = false

/** Replays queued mutations in order via `replay` (client.ts's own `gql`, passed in to avoid a circular
 *  import). Stops at the first failure — still offline, or a real error — and leaves the rest queued. */
export const processQueue = async (
  replay: (query: string, variables?: Record<string, unknown>) => Promise<unknown>,
  onItemSynced?: (label: string) => void,
): Promise<void> => {
  if (processing) return
  processing = true
  try {
    for (const item of read()) {
      try {
        await replay(item.query, item.variables)
        removeFromQueue(item.id)
        onItemSynced?.(item.label)
      } catch {
        break
      }
    }
  } finally {
    processing = false
  }
}
